import React, { useEffect, useState } from "react";
import { Fetch } from "../../shared/Fetch";
import { Toolbar } from "primereact/toolbar";
import { Button } from "primereact/button";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { InputSwitch } from "primereact/inputswitch";
import { Dialog } from "primereact/dialog";
import { Dropdown } from "primereact/dropdown";
import { MultiSelect } from "primereact/multiselect";
import { ICompanyMastDropdown } from "../../models/IDropdown";
import { ConfirmDialog, confirmDialog } from "primereact/confirmdialog";

export interface IGroup {
    id?: number;
    groupName: string;
    isActive?: boolean;
}

interface IProps {
    setModal: (modal: { open: boolean; singleData: IGroup }) => void;
    reload: boolean;
    toast: any
}

export const Table = ({ reload, setModal, toast }: IProps) => {
    const [data, setData] = useState<IGroup[]>([]);
    const [shareModal, setShareModal] = useState(false);
    const [selectedGroup, setSelectedGroup] = useState<IGroup | null>(null);
    const [companies, setCompanies] = useState<Array<ICompanyMastDropdown>>();
    const [branches, setBranches] = useState<any[]>([]);
    const [selectedCompany, setSelectedCompany] = useState<number | null>(null);
    const [selectedBranches, setSelectedBranches] = useState<number[]>([]);
    const [mappingList, setMappingList] = useState<any[]>([]);
    const [loading, setLoading] = useState(false);
    const [tableloading, settableLoading] = useState(false);


    const loadData = async () => {
        setLoading(true);
        const res = await Fetch("BranchAllocation_Setting/getBranchAllocation", "GET", null, null, null);
        if (res) {
            const result = await res.json();
            setData(result || []);
        }
        setLoading(false);
    };

    const loadCompanies = async () => {
        const res = await Fetch("Dropdown/company", "GET", null, null, null);
        if (res) {
            const data: Array<ICompanyMastDropdown> = await res.json();
            setCompanies(data || []);
        }
    };

    const loadBranches = async (companyId: number | null) => {
        if (!companyId) {
            setBranches([]);
            return;
        }

        const response = await Fetch("Dropdown/multiCompbranches", "GET", null, `company_id=${companyId}`, null);
        if (response) {
            const data = await response.json();
            const emptyData = {
                branch_ID: 0,
                branchName: "-- SELECT --",
                company_ID: 0,
            };

            data.unshift(emptyData);
            setBranches(data || []);
        }
    };
    const loadMappings = async (groupId: number) => {
        settableLoading(true);
        const res = await Fetch(`BranchAllocation_Setting/getMapping/${groupId}`, "GET", null, null, null);
        if (res) {
            const data = await res.json();
            setMappingList(data || []);
        }
        settableLoading(false)
    };

    useEffect(() => {
        loadData();
    }, [reload]);

    const openNew = () => {
        setModal({ open: true, singleData: { groupName: "" } });
    };

    const leftToolbarTemplate = () => (
        <Button label="New" icon="pi pi-plus" className="p-button-success" onClick={openNew} />
    );

    const toggleStatus = async (rowData: IGroup) => {
        const updated = !rowData.isActive;

        setData(prev =>
            prev.map(x => (x.id === rowData.id ? { ...x, isActive: updated } : x))
        );

        await Fetch("BranchAllocation_Setting/updateStatus", "PUT", {
            id: rowData.id,
            isActive: updated
        }, null, null);
    };

    const openShareModal = async (rowData: IGroup) => {
        setSelectedGroup(rowData);
        setSelectedCompany(null);
        setSelectedBranches([]);
        setShareModal(true);
        await loadCompanies();
        await loadMappings(rowData.id!);
    };

    const onCompanyChange = async (e: any) => {
        const companyId = e.value;
        setSelectedCompany(companyId);
        setSelectedBranches([]);
        await loadBranches(companyId);
    };

    const handleSubmit = async () => {
        setLoading(true)
        const payload = selectedBranches.map(branchId => ({
            groupId: selectedGroup?.id,
            companyId: selectedCompany,
            branchId: branchId
        }));

        const res = await Fetch("BranchAllocation_Setting/saveMapping", "POST", payload, null, null);

        if (res && res.ok) {
            const data = await res.json();

            const severity =
                data.skipped.length > 0 && data.added.length === 0
                    ? "error" : data.added.length > 0 ? "success" : "info";

            toast?.current?.show({
                severity, summary: "Result",
                detail: (
                    <div>
                        <ul style={{ paddingLeft: "15px" }}>
                            {data.skipped.map((x: any, i: number) => (
                                <li key={i} style={{ color: "red" }}>
                                    {x.type === "invalid" && ( <>{x.text}</> )}
                                    {x.type === "duplicate" && (  <> {x.text} (Already exists in <b>{x.groupName}</b>) </> )}
                                </li>
                            ))}
                        </ul>
                    </div>
                ), life: 4000
            });
            setSelectedBranches([]);
            await loadMappings(selectedGroup?.id!);
        }
        setLoading(false)
    };

    const handleDelete = (rowData: any) => {
        confirmDialog({
            message: `Remove "${rowData.branchNames}" from this group?`,
            header: "Confirm Delete",
            icon: "pi pi-exclamation-triangle",
            acceptClassName: "p-button-danger",

            accept: async () => {
                try {
                    const res = await Fetch(`BranchAllocation_Setting/deleteMapping/${rowData.id}`, "DELETE", null, null, null);
                    if (res && res.ok) {
                        setMappingList(prev => prev.filter(x => x.id !== rowData.id));
                        toast?.current?.show({ severity: "success", summary: "Deleted", detail: "Delete Company/Branch Successfully", life: 3000 });
                    }
                } catch {
                }
            }
        });
    };

    const actionBody = (rowData: IGroup) => (
        <div className="flex gap-3">
            <i className="pi pi-pencil text-primary" style={{ cursor: "pointer" }} onClick={() => setModal({ open: true, singleData: rowData })} />
            <i className="pi pi-share-alt text-green-500" style={{ cursor: "pointer" }} onClick={() => openShareModal(rowData)} />
        </div>
    );

    return (
        <>
            <ConfirmDialog />
            <Toolbar className="mb-2" left={leftToolbarTemplate} />
            <DataTable value={data} paginator rows={10} size="small" loading={loading}>
                <Column header="S.No" body={(_, opt) => opt.rowIndex + 1} />
                <Column field="groupName" header="Group Name" />
                <Column header="Status" body={(rowData: IGroup) => (
                    <InputSwitch checked={rowData.isActive === true} onChange={() => toggleStatus(rowData)} />
                )}
                />
                <Column header="Action" body={actionBody} />
            </DataTable>

            <Dialog
                header={`Share "${selectedGroup?.groupName}"`}
                visible={shareModal}
                style={{ width: "60vw", height: "40vw" }}
                modal
                onHide={() => setShareModal(false)}
            >
                <div className="flex gap-3 mb-3">

                    <Dropdown
                        value={selectedCompany}
                        options={companies}
                        onChange={onCompanyChange}
                        optionLabel="company_Name"
                        optionValue="company_ID"
                        placeholder="Select Company"
                        className="w-full"
                    />

                    <MultiSelect
                        value={selectedBranches}
                        options={branches}
                        onChange={(e) => setSelectedBranches(e.value)}
                        optionLabel="branchName"
                        optionValue="branch_ID"
                        placeholder="Select Branches"
                        display="chip"
                        className="w-full"
                        style={{ maxHeight: "40px", overflow: "auto" }}
                    />
                    <div className="flex justify-content-end">
                        <Button label="Submit" icon="pi pi-plus" className="p-button-success" onClick={handleSubmit} disabled={!selectedCompany || !selectedBranches.length} loading={tableloading} />
                    </div>
                </div>

                <DataTable value={mappingList} size="small" showGridlines loading={tableloading}>
                    <Column header="S.No" body={(_, opt) => opt.rowIndex + 1} />
                    <Column field="companyName" header="Company" />
                    <Column field="branchNames" header="Branches" />
                    <Column header="Action" body={(rowData) => (
                        <Button icon="pi pi-trash" className="p-button-danger p-button-text" onClick={() => handleDelete(rowData)} />)}
                    />
                </DataTable>
            </Dialog>
        </>
    );
};


